/**
 * Frontend file compression utilities
 * Compresses files before uploading to maintain consistency with backend processing
 */

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  wasOriginallyZip: boolean;
  isCompressed: boolean;
  compressionType: string | null;
}

export interface CompressionProgress {
  progress: number;
  stage: string;
}

/**
 * Detect if a file is already a ZIP file based on file extension and MIME type
 */
export function detectZipFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();
  
  // Check file extension
  const isZipByExtension = fileName.endsWith('.zip');
  
  // Check MIME type
  const isZipByMimeType = mimeType === 'application/zip' || 
                         mimeType === 'application/x-zip-compressed';
  
  return isZipByExtension || isZipByMimeType;
}

/**
 * Check if file has .bak extension (SQL Server backup file)
 */
export function isBakFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.bak');
}

/**
 * Create a ZIP file containing the input file using browser APIs
 */
export async function compressFileToZip(
  file: File,
  onProgress?: (progress: CompressionProgress) => void
): Promise<File> {
  try {
    // Import JSZip dynamically to reduce bundle size
    const JSZip = (await import('jszip')).default;
    
    if (onProgress) {
      onProgress({ progress: 10, stage: 'Initializing compression...' });
    }
    
    const zip = new JSZip();
    
    if (onProgress) {
      onProgress({ progress: 20, stage: 'Reading file...' });
    }
    
    // Read file as array buffer
    const fileBuffer = await file.arrayBuffer();
    
    if (onProgress) {
      onProgress({ progress: 40, stage: 'Adding file to archive...' });
    }
    
    // Add file to ZIP with original name
    zip.file(file.name, fileBuffer);
    
    if (onProgress) {
      onProgress({ progress: 60, stage: 'Compressing file...' });
    }
    
    // Generate ZIP file
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6 // Compression level 0-9 (6 is good balance)
      }
    }, (metadata) => {
      // Update progress during compression
      if (onProgress) {
        const compressionProgress = 60 + (metadata.percent * 0.35); // 60% to 95%
        onProgress({ 
          progress: compressionProgress, 
          stage: `Compressing... ${Math.round(metadata.percent)}%` 
        });
      }
    });
    
    if (onProgress) {
      onProgress({ progress: 100, stage: 'Compression complete' });
    }
    
    // Create new File object from compressed blob
    const originalNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
    const compressedFileName = `${originalNameWithoutExt}.zip`;
    
    const compressedFile = new File([zipBlob], compressedFileName, {
      type: 'application/zip',
      lastModified: Date.now()
    });
    
    return compressedFile;
    
  } catch (error) {
    console.error('Compression failed:', error);
    throw new Error(`Failed to compress file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Process file for upload - detect type and compress if needed
 */
export async function processFileForUpload(
  file: File,
  onProgress?: (progress: CompressionProgress) => void
): Promise<CompressionResult> {
  const originalSize = file.size;
  
  try {
    // Stage 1: Detect file type
    if (onProgress) {
      onProgress({ progress: 5, stage: 'Analyzing file type...' });
    }
    
    const isAlreadyZip = detectZipFile(file);
    
    if (isAlreadyZip) {
      // File is already a ZIP, no need to compress
      if (onProgress) {
        onProgress({ progress: 100, stage: 'File already compressed' });
      }
      
      console.log('File is already a ZIP, skipping compression:', {
        fileName: file.name,
        size: file.size,
        type: file.type
      });
      
      return {
        file,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 1.0,
        wasOriginallyZip: true,
        isCompressed: false,
        compressionType: null
      };
    }
    
    // Stage 2: Compress the file if it's a .bak file or large file (but not too large)
    const maxCompressionSize = 1 * 1024 * 1024 * 1024; // 1GB limit for browser compression
    const shouldCompress = (isBakFile(file) || file.size > 10 * 1024 * 1024) && file.size <= maxCompressionSize;
    
    if (!shouldCompress) {
      // Files that don't need compression or are too large for browser compression
      const isTooBig = file.size > maxCompressionSize;
      const stage = isTooBig 
        ? 'Large file - uploading without compression' 
        : 'File ready for upload';
      
      if (onProgress) {
        onProgress({ progress: 100, stage });
      }
      
      if (isTooBig) {
        console.log('File too large for browser compression, uploading as-is:', {
          fileName: file.name,
          size: file.size,
          maxCompressionSize,
          sizeMB: Math.round(file.size / (1024 * 1024))
        });
      }
      
      return {
        file,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 1.0,
        wasOriginallyZip: false,
        isCompressed: false,
        compressionType: null
      };
    }
    
    // Compress the file
    const compressedFile = await compressFileToZip(file, onProgress);
    const compressedSize = compressedFile.size;
    const compressionRatio = compressedSize > 0 ? originalSize / compressedSize : 1.0;
    
    console.log('File compression completed:', {
      originalFileName: file.name,
      compressedFileName: compressedFile.name,
      originalSize,
      compressedSize,
      compressionRatio: compressionRatio.toFixed(2),
      spaceSaved: ((1 - (compressedSize / originalSize)) * 100).toFixed(1) + '%'
    });
    
    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      compressionRatio,
      wasOriginallyZip: false,
      isCompressed: true,
      compressionType: 'zip'
    };
    
  } catch (error) {
    console.error('Error processing file for upload:', {
      fileName: file.name,
      originalSize,
      error: error instanceof Error ? error.message : error
    });
    
    // If compression fails, return original file
    if (onProgress) {
      onProgress({ progress: 100, stage: 'Using original file (compression failed)' });
    }
    
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 1.0,
      wasOriginallyZip: false,
      isCompressed: false,
      compressionType: null
    };
  }
}

/**
 * Calculate compression statistics
 */
export function calculateCompressionStats(originalSize: number, compressedSize: number) {
  const ratio = originalSize / compressedSize;
  const spaceSavedBytes = originalSize - compressedSize;
  const spaceSavedPercentage = compressedSize > 0 ? ((spaceSavedBytes / originalSize) * 100) : 0;
  
  return {
    compressionRatio: ratio,
    spaceSavedBytes,
    spaceSavedPercentage: Math.max(0, spaceSavedPercentage),
    isEffective: spaceSavedPercentage > 5 // Consider compression effective if saves >5%
  };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
